import { faSearch, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Flex, Input, InputProps, Text } from '@modusign/design-system';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

import { UPLOAD_FILE_BYTE } from '../../constants';
import FileSelectErrorCode from '../../types/FileSelectErrorCode';
import IconLinkButton from '../IconLinkButton';

interface PDFSelectorProps {
  selectedFile: File | undefined;
  onFileSelect: (file: File | undefined) => void;
  onError: (errorCode: FileSelectErrorCode) => void;
}

export default function PDFSelector({ selectedFile, onFileSelect, onError }: PDFSelectorProps) {
  const isSelected = !!selectedFile;
  const { isDragActive, inputRef, open, getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: false,
    maxSize: UPLOAD_FILE_BYTE,
    noClick: true,
    useFsAccessApi: false,
    onDropAccepted: (_files: File[]) => onFileSelect(_files[0]),
    onDropRejected: (fileRejections) =>
      onError(fileRejections[0]?.errors[0]?.code as FileSelectErrorCode),
  });

  const handleDeselectFile = useCallback(() => {
    if (inputRef.current == null) {
      return;
    }
    inputRef.current.files = null;
    onFileSelect(undefined);
  }, [inputRef, onFileSelect]);

  return (
    <Flex
      {...getRootProps()}
      alignItems="center"
      backgroundColor="boxBackgroundNeutralLight"
      borderColor={isDragActive ? 'boxBorderEmphasisBase' : 'boxBorderNeutralBase'}
      borderRadius="3px"
      borderStyle={isSelected ? 'solid' : 'dashed'}
      borderWidth={1}
      flexDirection="column"
      height={200}
      justifyContent="center"
      position="relative"
      sx={{
        ':hover': {
          backgroundColor: 'boxBackgroundNeutralSlightlyLight',
        },
      }}
    >
      <Input {...(getInputProps() as InputProps)} data-testid="pdf-file-input" />
      {isDragActive && <DraggingOverlay />}
      {isSelected ? (
        <FileSelectedStatus selectedFile={selectedFile} onFileDeselect={handleDeselectFile} />
      ) : (
        <DefaultStatus onFileSelect={open} />
      )}
    </Flex>
  );
}

function DraggingOverlay() {
  return (
    <Flex
      alignItems="center"
      backgroundColor="boxBackgroundEmphasisBase"
      borderRadius="3px"
      color="textEmphasisBase"
      flexDirection="column"
      fontSize="md"
      fontWeight="medium"
      height="100%"
      justifyContent="center"
      position="absolute"
      width="100%"
    >
      여기에 파일을 놓아 주세요.
    </Flex>
  );
}

function FileSelectedStatus({
  selectedFile,
  onFileDeselect,
}: {
  selectedFile: File | undefined;
  onFileDeselect: () => void;
}) {
  return (
    <>
      <Text
        color="textNeutralBase"
        display="block"
        fontSize="md"
        fontWeight="medium"
        mb={2}
        sx={{
          textOverflow: 'ellipsis',
          maxWidth: '80%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {selectedFile?.name}
      </Text>
      <IconLinkButton icon={faXmark} onClick={onFileDeselect}>
        선택 취소
      </IconLinkButton>
    </>
  );
}

function DefaultStatus({ onFileSelect }: { onFileSelect: () => void }) {
  return (
    <>
      <Text color="textNeutralBase" fontSize="md" fontWeight="medium" mb={1}>
        문서 선택
      </Text>
      <Text color="textNeutralLight" fontSize="md" fontWeight="regular" mb={1}>
        이곳에 PDF 파일을 끌어다 놓거나
      </Text>
      <IconLinkButton icon={faSearch} onClick={onFileSelect}>
        파일 찾기
      </IconLinkButton>
    </>
  );
}
